/* Simplified Chinese — see .skills/i18n-workflow/SKILL.md */
import type { Resources } from './en';

export const zhCN: Resources = {
  common: {
    brand: 'Nexnovar',
    nav: {
      product: '产品',
      login: '登录',
      back: '返回',
    },
    cta: {
      primary: '开始使用',
      secondary: '了解更多',
      example: '看一个完整示例 →',
      login: '登录 Nexnovar',
    },
    footer: {
      rights: '保留所有权利。',
    },
  },
  home: {
    stats: {
      eyebrow: '用数据说话',
      title: '可扩展的信任基础设施',
      items: [
        { value: 1247, suffix: '', label: '已还原决策' },
        { value: 100, suffix: '%', label: '证据完整率' },
        { value: 3, suffix: '秒', label: '证据包生成时间' },
      ],
    },
    painPoints: {
      eyebrow: '现实情况',
      title: '证据散落在五个互不打通的系统里',
      body: '合规官要从邮件、共享盘、归档 PDF 和电子表格里拼凑审计材料——每次审计都是新一轮搜寻。',
      essence: '问题不是“机构是否合规”，而是“机构能否证明合规”。',
      timeline: {
        eyebrow: '合规官 · 周二下午 15:42',
        items: [
          'Slack #compliance —— 有人能确认一下 #4219 的 KYC 等级吗？',
          '邮件 · 外部律师 · 2 天前 · 仍未读',
          '电子表格 · Issuance-tracker-Q3.xlsx · 14 个表 · 6 小时前最后一次编辑',
          'Notion —— 监管反馈（待定） · 空',
        ],
      },
      bodyAlt: '搜寻越久，罚款风险越高，真正重要的工作反而没时间做。',
      narrative: {
        businessOfficer: {
          eyebrow: '业务决策者',
          quote:
            '六个月后让我重建当时为什么批这笔贷款？我已经想不起来了——备注散落在邮件、表格和记忆里。',
        },
        partner: {
          eyebrow: '客户与合作方',
          quote: '每次入驻都填同一套六张表。每个季度，不同团队再来要一遍同样的资料。',
        },
      },
    },
    cta: {
      title: '让信任证据成为可管理的资产',
      body: '不再在截止日期的压力下拼凑审计材料，而是从每一次决策落地的瞬间就开始积累。',
      primaryCta: '登录 Nexnovar',
      contactPrompt: '合作咨询？写邮件给我们：',
    },
    hero: {
      eyebrow: '信任操作系统',
      title: 'Nexnovar',
      lede: '让信任成为可管理的资产',
      tagline: '为审计团队打造的决策证据自动化平台。',
      subtitle:
        '无论是银行的审计合规、跨境贸易的单证流转，还是研究机构的资质评估——Nexnovar 让信任证据变成可积累、可管控、可调用的资产。',
      tags: {
        banking: '银行',
        trade: '贸易',
        research: '研究',
      },
      contactPrompt: '合作咨询？写邮件给我们：',
    },
    intro: {
      kicker: 'Nexnovar',
      title: '信任操作系统',
      lede: '让“信任证据”变成可积累、可管控、可调用的资产。',
      pillars: [
        {
          title: '信任资产化',
          body: '把 KYC、资质、授权等证据从一次性消耗品升级为可复用的数字资产。',
        },
        {
          title: '一次验证，多处复用',
          body: '验证结果在授权范围内被多个机构调取——无需重复提交。',
        },
        {
          title: '授权即边界',
          body: '每次数据访问需明确授权，证据链完整，数据不动验证流动。',
        },
        {
          title: '合规即默认',
          body: '政策变更秒级扫描，过期信号自动管理，合规成为默认属性。',
        },
      ],
      footnote: '银行、跨境贸易、研究机构、政府治理——场景不同，信任逻辑相同。',
      // roleMapping:
      //   '为客户提供4 个角色：信任身份（NovarPass）、信任资产（NovarVault）、授权与留痕（NovarConsent + NovarAnchor）、AI + LCM + 审计（NovarAIScan + NovarLCM + NovarAudit）。',
      contactPrompt: '想深入了解？',
    },
    capabilities: {
      eyebrow: '平台',
      kicker: 'Nexnovar Platform',
      title: '七大核心能力',
      lede: '信任资产的完整生命周期管理',
      items: [
        { title: '一次 KYC，多处复用' },
        { title: '授权即边界，证据带 ID' },
        { title: '个人授权，数据不动' },
        { title: '决策不可篡改，永久可溯' },
        { title: '政策升级秒级扫描' },
        { title: '过期信号自动汇总' },
      ],
      auditTitle: '一键合规自证',
      auditBody: '历史决策一键自证',
    },
    synergy: {
      kicker: '能力协作',
      title: '七大能力如何协同工作',
      lede: '从信任建立到审计自证的完整闭环',
      modules: [
        { name: 'NovarPass', purpose: '建立信任身份', tags: ['护照', '地址', '学历'] },
        { name: 'NovarVault', purpose: '存储信任资产', tags: ['合同', '研究', '票据'] },
        { name: 'NovarConsent', purpose: '控制授权边界', tags: ['范围可控', '可审计'] },
        { name: 'NovarAnchor', purpose: '锚定信任决策', tags: ['不可篡改', '带时间戳'] },
        { name: 'NovarAIScan', purpose: '监测政策变化', tags: ['政策比对', '异常扫描'] },
        { name: 'NovarLCM', purpose: '维护生命周期', tags: ['到期提醒', '自动续期'] },
        { name: 'NovarAudit', purpose: '准备审计自证', tags: ['一键自证', '3 秒成包'] },
      ],
    },
    synergyAnimation: {
      title: '七大能力如何协同工作',
      subline: '从信任建立到审计自证的完整闭环。',
      avatar: 'Alice · 个人账户',
      stages: {
        s1: '标题开场——七项能力，一个闭环。',
        s2: '标题粒子化——NovarPass（蓝）与 NovarVault（紫）在两侧凝成卡片。',
        s3: '底部中央 Organization 入场——三层能量波向上扩散，提出证据请求。',
        s4: 'Pass 与 Vault 各发出一条流光线，递送 NovarConsent——✓ 徽标降落到请求者头顶。',
        s5: '两个 NovarAnchor 金点锚定闭环——Pass、Vault 与请求者被永久绑定。',
        s6: 'NovarLCM 大圆环包住全部，开始顺时针旋转——NovarAIScan 出现于右侧。',
        s7: '圆环与 AIAgent 之间虚线脉冲通讯——线上文字轮换显示：expiry check / renewal / policy diff / anchor verify / anomaly scan。',
        s8: '当文字恰好为 "audit"——三圈红环扩散，NovarAudit 徽标点亮；AIAgent 同步高亮。',
      },
    },
    scenario: {
      kicker: '完整场景',
      title: '一家银行使用 Nexnovar 的一天',
      steps: [
        {
          title: '客户开户：NovarPass 完成 KYC',
          body: '客户 Alice 首次开户，一次 KYC 验证后，结果存入 NovarVault。',
        },
        {
          title: '申请贷款：NovarConsent 记录授权',
          body: 'Alice 授权银行验证她的信用状态——机构只获得验证状态，不接触原始数据。',
        },
        {
          title: '审批决策：NovarAnchor 锚定证据',
          body: 'John 批准贷款时，决策被加密锚定——包含当时的政策版本和时间戳。',
        },
        {
          title: 'AML 政策升级：NovarAIScan 自动扫描',
          body: 'RBNZ 发布新政策，NovarAIScan 秒级识别所有受影响客户。',
        },
        {
          title: '客户维护：NovarLCM 监控有效期',
          body: 'Alice 的 KYC 即将过期前 30 天，NovarLCM 自动预警并发起续期流程。',
        },
        {
          title: 'RBNZ 审计：NovarAudit 一键生成证据包',
          body: '3 秒还原 1,247 笔决策的完整证据链，审计人员可独立验证。',
        },
      ],
    },
    auditNotice: {
      kicker: '周一上午 9:30',
      title: 'RBNZ 发来一封审计通知',
      emailBadge: '📨 紧急审计通知',
      toLabel: '收件人：',
      toValue: 'Bob Wilson（合规官）',
      fromLabel: '发件人：',
      fromValue: 'RBNZ 审计部',
      subject: '2023 Q4 贷款审批合规审查',
      body: '请在 7 个工作日内，提交 2023 年第四季度全部贷款审批决策的合规证据包。',
      totalLabel: '决策总数',
      totalValue: '1,247',
      fineRisk: '罚款风险',
      fineAmount: '5M',
      fineCurrency: '纽元（最高）',
      caption: '"没有合规团队愿意收到的邮件。"',
    },
    bobsDilemma: {
      kicker: 'Bob 的困境',
      title: '415 小时的工作量，7 天的时间',
      systemsLabel: 'Bob 需要搜索的系统：',
      systems: ['遗留邮件系统', '贷款管理系统', 'PDF 档案', '共享盘', 'Excel 表格'],
      required: '所需时间',
      requiredValue: '小时',
      requiredCalc: '= 1,247 × 20 分钟',
      available: '可用时间',
      availableValue: '天 = 168 小时',
      gapLabel: '缺口',
      gapValue: '247 小时',
      footnote: '即使不眠不休，也完不成。',
    },
    cost: {
      kicker: '合规失败的代价',
      title: '不只是罚款',
      items: [
        { label: 'AML 失败', amount: '13 亿' },
        { label: '误导客户', amount: '25 亿' },
      ],
      footnote1: '罚款只是看得见的成本。',
      footnote2: '声誉损失、客户流失、监管升级才是隐性代价。',
      quote: '"合规是银行的底线。合规的核心是：你能够证明自己做了该做的事。"',
    },
    novaAudit: {
      kicker: 'NovarAudit · 核心能力',
      title: '3 秒还原 1,247 笔决策',
      stat: '3 秒',
      statLabel: '1,247 笔决策的完整证据包',
      items: [
        { value: '1,247', label: '决策总数' },
        { value: '100%', label: '可追溯' },
        { value: '2.4', suffix: 'GB', label: '证据体积' },
      ],
    },
    decisionDetail: {
      kicker: 'NovarAudit · 决策详情',
      title: '每笔决策都被还原到当时的完整状态',
      fields: [
        { label: '决策编号', value: '#DEC-2023-04217' },
        { label: '决策时间', value: '2023-10-15 14:23:47 UTC+13' },
        { label: '决策人', value: 'John Smith（高级信贷经理）' },
        { label: '决策内容', value: '批准 450,000 纽元按揭贷款' },
        { label: '证据来源', value: '贷款管理系统 v3.2（只读）' },
        { label: '当时政策版本', value: '贷款政策 v1.2.0（生效 2023-10-01）' },
        { label: '锚定哈希', value: '0x9f4a2c8b7e1d...f3a' },
        { label: '信任签名', value: '已验证 · Ed25519' },
      ],
      actions: ['查看全部 1,247 笔', '导出报告'],
    },
    trustVerify: {
      kicker: 'NovarAudit · 信任验证',
      title: '审计人员不需要信任 Bob——他们可以自己验证',
      registryTitle: '信任注册验证',
      placeholder: '输入锚定哈希',
      verify: '验证',
      checks: [
        { question: '决策内容是否被篡改？', answer: '否' },
        { question: '政策版本是否当时生效？', answer: '是（v1.2.0）' },
        { question: '决策人是否真实？', answer: '是（John Smith）' },
        { question: '证据链是否完整？', answer: '是' },
      ],
      quote: '"信任是可验证的——不是通过‘相信’，而是通过‘证明’。"',
    },
    auditComplete: {
      kicker: 'NovarAudit · 完成',
      title: '周一上午 9:33 提交全部证据包',
      confirmation: 'RBNZ 审计确认：',
      results: ['1,247 笔决策 — 100% 可追溯', '0 个合规问题', '审计通过', '0 罚款'],
      before: '9:30 收到通知 →',
      after: '9:33 提交证据包',
      footnote: '3 分钟 vs 传统数周',
      quote: '合规不再是银行的噩梦，而是日常运营的一部分。',
      boundary: '让审计人员更快、更准确地找到可疑点，而不是替代专业判断。',
      backToHome: '返回首页',
    },
    useCases: {
      kicker: '应用场景',
      title: '同一套信任基础设施，支撑多个行业',
      items: [
        {
          industry: '银行审计',
          topic: '合规自证',
          body: 'RBNZ 审计期间，3 秒生成证据包。',
        },
        {
          industry: '跨境贸易',
          topic: '单证流转',
          body: '出口商提交一次，多方接收。',
        },
        {
          industry: '研究协作',
          topic: '资质复用',
          body: '研究者验证一次，多项目复用。',
        },
        {
          industry: '政府',
          topic: '多部门协同',
          body: '部门间共享信息，数据不动，验证流动。',
        },
        {
          industry: '保险审计',
          topic: '理赔证据',
          body: '每一笔理赔决策都有完整证据链，随时接受监管审视。',
        },
        {
          industry: '医疗',
          topic: '诊疗审计',
          body: '诊疗决策与同意书，可按需完整重建。',
        },
        {
          industry: '制药',
          topic: '试验合规',
          body: '协议版本、同意签名与决策在每一步都被完整记录。',
        },
      ],
    },
    thankYou: {
      title: '感谢观看',
      lede: '让信任成为可管理的资产',
      tagline: 'Trust, No Compromise.',
      contactPrompt: '合作咨询？写邮件给我们：',
    },
    architecture: {
      eyebrow: '我们的接入方式',
      title: 'Nexnovar 是外挂，不是替代。',
      lede: '核心系统保持原样。我们通过 API 进入——数据留在你的环境；只有被授权的调用方，才能看到你授权过的指纹。',
      steps: [
        {
          number: '01',
          title: '核心系统保持原样',
          body: '银行核心、保险理赔、医院系统、政府预算——都不改动。',
        },
        {
          number: '02',
          title: '通过 API 进入',
          body: '适配代码（你的或我们的）把现有系统包起来，把 Nexnovar 需要的事件传出来。',
        },
        {
          number: '03',
          title: '身份与资产作为证据入驻',
          body: '身份证据（护照、KYC）与资产证据（合同、票据、资质）以密码学签名驻入 Nexnovar。',
        },
        {
          number: '04',
          title: '出去也走授权',
          body: '授权范围内的调用方只能读到被授权的指纹——而这次读取本身也会被留痕。',
        },
      ],
      footnote: '同一道门进出。任何数据离开你的环境，都必须经过明确授权。',
    },
    boundaryStatement: {
      eyebrow: '我们不做什么',
      title: '我们不做什么。',
      items: [
        { text: '不要求你重建现有系统。' },
        { text: '不替代审计师的专业判断。' },
        { text: '不把你的原始数据带出你的环境。' },
        { text: '不出信用评分，不做信贷决策。' },
        { text: '不抓外部数据（征信、公开记录、诉讼）。' },
      ],
      quote: '数据主权：原始数据不出客户环境。',
    },
    positioning: {
      kicker: '我们是什么 —— 不是什么',
      title: 'Nexnovar 不是信用评分系统。不是数据中台。不是通用 AI。',
      lede: '三类系统常被混淆。在四个最关键的维度上，我们讲清差别。',
      headers: {
        question: '维度',
        credit: '信用评分系统',
        data: '数据中台',
        ai: '通用 AI',
        nexnovar: 'Nexnovar',
      },
      rows: [
        {
          question: '数据住在哪里？',
          credit: '外部征信调取',
          data: '集中式副本',
          ai: '喂进去什么算什么',
          nexnovar: '留在你的环境',
        },
        {
          question: '谁能读到？',
          credit: '征信机构 + 你',
          data: '平台运维',
          ai: '模型供应商',
          nexnovar: '只有被授权的调用方，且只读指纹',
        },
        {
          question: '做什么决定？',
          credit: '一个信用分',
          data: '不做决定 —— 只存储',
          ai: '提示什么算什么',
          nexnovar: '不做决定 —— 只记录与核验',
        },
        {
          question: '产出是什么？',
          credit: '一个不透明的分',
          data: '查询结果',
          ai: '生成文字 / 判断',
          nexnovar: '一份带签名的证据包，带时间戳',
        },
      ],
    },
    dataApproach: {
      eyebrow: '数据策略',
      title: '原始数据从不出你的环境。',
      lede: '我们只存三类指纹。三种对接方式——选最适合你的栈。',
      doNotItems: [
        '不存储原始文档。',
        '不从征信、登记或外部来源拉取。',
        '不出信用评分或任何第三方评级。',
        '不用你的数据训练模型。',
      ],
      weDoItems: [
        '决策指纹 —— 决策产生那一刻内容快照的哈希。',
        '证据源指纹 —— 指向决策原始来源系统的哈希。',
        '授权签名 —— 证明是谁在什么范围内授权了什么。',
      ],
      connectMethods: [
        {
          name: '系统只读',
          useCase: '面向记录型系统',
          description: '一条只读的数据库连接，事件一发生就流式推过来。',
        },
        {
          name: 'API',
          useCase: '面向现代栈',
          description: 'REST 接口，你的应用在决策那一刻调用。',
        },
        {
          name: '文件导入',
          useCase: '面向批量 / 遗留系统',
          description: '按计划把决策或证据作为文件投递过来，我们提取指纹。',
        },
      ],
    },
    capabilityBoundary: {
      eyebrow: '我们能做什么 —— 不能做什么',
      colCan: '能做',
      colCannot: '不能做',
      title: '边界清楚，没有意外。',
      canDo: [
        '3 秒还原 1,247 笔决策的证据链。',
        '自动标记已有决策流程中的异常。',
        '用密码学签名维护不可篡改的审计轨迹。',
        '在授权范围内让验证过的证据被多个机构复用。',
        '24 小时内扫描监管政策变化并映射影响。',
      ],
      cannotDo: [
        '判断决策是否正确。',
        '替代审计师的专业判断。',
        '输出信用评分或第三方评级。',
        '要求你重建现有系统。',
        '把原始数据带出你的环境。',
      ],
      quote: '让审计人员更快、更准确地找到可疑点，而不是替代专业判断。',
    },
    complianceReady: {
      eyebrow: '合规状态',
      title: '为受监管环境而生。',
      lede: '待法务签字 —— 占位文案，未通过法律审核前不发布。',
      items: [
        {
          standard: 'FMA 审计质量（新西兰）',
          status: '待法务审核',
          note: '金融市场管理局审计标准。',
        },
        {
          standard: '新西兰隐私法 2020',
          status: '待法务审核',
          note: '新西兰隐私原则。',
        },
        {
          standard: 'GDPR（欧盟）',
          status: '待法务审核',
          note: '被遗忘权、数据最小化、境内部署可选项。',
        },
        {
          standard: 'ISO 27001',
          status: '待法务审核',
          note: '信息安全管理体系认证。',
        },
      ],
    },
    governancePolicy: {
      eyebrow: '可扩展性',
      title: 'Governance Policy —— 你的规则，你的行业。',
      lede: '千行百业都有自己的治理规则。我们给你一个地方定义它们 —— 同时我们正在共建一个规则知识库。',
      pillars: {
        selfDefined: {
          title: '自建规则',
          body: '基于你所处场景的合规要求，定义一套适合自己的治理规则。',
        },
        library: {
          title: '知识库（预留）',
          body: '未来，将由社区共建治理规则知识库，你可以选用现成规则。',
          badge: '敬请期待 · POC 阶段预留',
        },
      },
      footnote: '这一层在 POC 阶段作为扩展预留；社区共建知识库随后再上线。',
    },
  },
  login: {
    title: '登录 Nexnovar',
    subtitle: '请使用工作邮箱。企业账号已启用单点登录。',
    form: {
      emailLabel: '工作邮箱',
      emailPlaceholder: 'you@company.com',
      passwordLabel: '密码',
      passwordPlaceholder: '请输入密码',
      rememberMe: '保持登录状态',
      submit: '登录',
      forgotPassword: '忘记密码？',
      backToHome: '返回首页',
    },
    sso: {
      divider: '或',
      google: '使用 Google 账号继续',
      microsoft: '使用 Microsoft 账号继续',
    },
    note: {
      title: '提示',
      body: '认证功能尚未接入后端。本页面是未来的入口，目前仅渲染登录界面。',
    },
    footer: '无法登录？请联系你的工作区管理员。',
    contactPrompt: '想深入了解？',
    aside: {
      title: '让合规变成可编程。',
      body: 'Nexnovar 把复杂的监管要求，转化为可审计、可编程的信任。',
      list: {
        auditTrail: '一份监管可直接读取的统一审计轨迹。',
        reserveAttestations: '实时储备金证明。',
        policyAsCode: '在签名那一刻即生效的策略即代码。',
      },
    },
  },
  notFound: {
    title: '404',
    body: '找不到该页面。',
    cta: '返回首页',
  },
};
